import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useDataset, useFHIRQuery } from '../../../../util/api';
import { formatCodingToken } from '@lifeomic/fhir-constants';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import { UseSurveysListProvider } from '@lifeomic/phc-web-toolkit/src/components/ManageSurveys/SurveysList/useSurveysList';
import {
  fetchSnapshotDataSourcesForPatients,
  mapSnapshotDataToPatient,
} from '../../OnboardingSnapshot/dataSources';
import { formatSnapshots } from '../../OnboardingSnapshot/components/OnboardingSnapshotTable';
import { IndividualOnboardingView } from '../../OnboardingSnapshot/components/IndividualOnboardingView';
import { formatMember } from '../../../workflows/utils';
import { fhirSystems } from '../../OnboardingSnapshot/fhir-systems';
import type { MemberOnboardingData } from '../../OnboardingSnapshot';
import { WithPlatformDefaults } from '@lifeomic/react-client';

const useStyles = makeStyles(theme => ({
  pageContent: {
    flex: 1,
    minHeight: 0,
    backgroundColor: theme.palette.common.white,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
}));

type OnboardingSnapshotContentProps = {
  member: WithPlatformDefaults<fhir3.Patient> | null;
  memberId: string;
  fallback: React.ReactNode;
};

export const OnboardingSnapshotContent: React.FC<
  OnboardingSnapshotContentProps
> = ({ member, memberId, fallback }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const dataset = useDataset();

  const physiciansQuery = useFHIRQuery('GET /v1/fhir/dstu3/Practitioner', {
    _tag: [
      dataset.codingToken,
      formatCodingToken({
        system: fhirSystems.practitioner.role.system,
        code: fhirSystems.practitioner.role.values.physician,
      }),
    ],
    pageSize: 1000,
  });

  const onboardingQuery = useQuery({
    queryKey: [
      'member-onboarding-data',
      memberId,
      dataset?.id,
      physiciansQuery.data,
    ],
    queryFn: async (): Promise<MemberOnboardingData | null> => {
      if (!memberId || !member || !dataset) return null;
      const referenceDate = new Date();
      const allData = await fetchSnapshotDataSourcesForPatients({
        currentPatientIds: memberId,
        dataset,
        referenceDate,
      });
      const patientData = mapSnapshotDataToPatient(allData, member?.id ?? '');
      const providers = physiciansQuery.data
        ? unwrapBundle(physiciansQuery.data)
        : [];
      const combined = {
        patient: member,
        consents: patientData.consents,
        surveyResponses: patientData.surveyResponses,
        appointments: patientData.appointments,
        keyAppointments: patientData.keyAppointments,
        member: formatMember(member, {
          formatMessage,
          providers,
        }),
      };
      return {
        ...combined,
        snapshots: formatSnapshots(combined),
      };
    },
    enabled:
      Boolean(memberId) &&
      Boolean(member) &&
      Boolean(dataset) &&
      Boolean(physiciansQuery.data),
  });

  if (!member) {
    return <>{fallback}</>;
  }

  if (
    onboardingQuery.isLoading ||
    onboardingQuery.isError ||
    !onboardingQuery.data
  ) {
    return <>{fallback}</>;
  }

  return (
    <Box className={classes.pageContent}>
      <UseSurveysListProvider>
        <IndividualOnboardingView {...onboardingQuery.data} />
      </UseSurveysListProvider>
    </Box>
  );
};
